import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Download, PlusSquare, Share } from "lucide-react";
import { api, trackPwaInstall } from "../api";
import { useLang } from "../i18n";
import { usePwaInstall } from "../hooks/usePwaInstall";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";

export function IosInstallDialog({ open, onOpenChange }) {
  const { t } = useLang();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="install-ios-dialog">
        <DialogHeader>
          <DialogTitle>{t("install.ios_title")}</DialogTitle>
          <DialogDescription>{t("install.ios_sub")}</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3 pt-1 text-sm text-slate-700">
          <li className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100"><Share size={15} /></span>
            {t("install.ios_step1")}
          </li>
          <li className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100"><PlusSquare size={15} /></span>
            {t("install.ios_step2")}
          </li>
        </ol>
      </DialogContent>
    </Dialog>
  );
}

export default function InstallCta({ testId = "install-cta", buttonClass = "" }) {
  const { t } = useLang();
  const { canNativePrompt, isIos, installed, promptInstall } = usePwaInstall();
  const [iosOpen, setIosOpen] = useState(false);
  const viewFired = useRef(false);

  useEffect(() => {
    // one aggregate emission per browser session, survives SPA route remounts
    if (!installed && !viewFired.current) {
      viewFired.current = true;
      try {
        if (sessionStorage.getItem("pg_install_view")) return;
        sessionStorage.setItem("pg_install_view", "1");
      } catch { /* private mode — fall through and emit once per mount */ }
      api.post("/analytics/event", { event: "install_view" }).catch(() => {});
    }
  }, [installed]);

  if (installed) {
    return (
      <span data-testid={`${testId}-installed`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600">
        <Check size={15} /> {t("install.installed")}
      </span>
    );
  }

  const handleClick = async () => {
    api.post("/analytics/event", { event: "install_clicked" }).catch(() => {});
    if (canNativePrompt) {
      const outcome = await promptInstall();
      if (outcome === "accepted") { api.post("/analytics/event", { event: "install_accepted" }).catch(() => {}); trackPwaInstall(); }
    } else if (isIos) {
      setIosOpen(true);
      api.post("/analytics/event", { event: "install_ios_view" }).catch(() => {});
    }
    // no native prompt & not iOS -> button renders as a link to /aplikacja
  };

  const inner = (
    <>
      <Download size={15} className="mr-1.5" /> {t("install.cta")}
    </>
  );

  return (
    <>
      {canNativePrompt || isIos ? (
        <Button data-testid={testId} variant="outline" className={buttonClass} onClick={handleClick}>
          {inner}
        </Button>
      ) : (
        <Button data-testid={testId} variant="outline" className={buttonClass} asChild>
          <Link to="/aplikacja" onClick={() => api.post("/analytics/event", { event: "install_clicked" }).catch(() => {})}>
            {inner}
          </Link>
        </Button>
      )}
      <IosInstallDialog open={iosOpen} onOpenChange={setIosOpen} />
    </>
  );
}
